/**
 * Config-driven field definitions consumed by OnboardingFieldGuide.
 * Each FieldGuideConfig maps one-for-one onto a specific form's guided walkthrough.
 * Validation callbacks read the live DOM so configs stay decoupled from each
 * form's react-hook-form internals.
 *
 * i18n: the English copy below is the source and doubles as the render-time
 * fallback. OnboardingFieldGuide resolves each string through
 * `t('onboarding.guides.<guideId>.…', …, <english>)`, so the Indonesian
 * translations live in `id.ts` while English stays co-located with the
 * selectors and validators it describes.
 */
function visibleEl(selector: string): HTMLElement | null {
  const els = Array.from(document.querySelectorAll<HTMLElement>(selector));
  for (const el of els) {
    if (el.getBoundingClientRect().width > 0) return el;
  }
  return els[0] ?? null;
}

function inputValue(selector: string): string {
  const el = visibleEl(selector) as HTMLInputElement | HTMLTextAreaElement | null;
  return (el?.value ?? '').trim();
}

export interface FieldDef {
  key: string;
  selector: string;
  label: string;
  instruction: string;
  validate: () => boolean;
  /** Action steps (e.g. "click Save as Draft", "click Auto-Calculate") whose
   *  spotlighted control disappears or whose result lands elsewhere: advance
   *  automatically the moment validate() passes, instead of waiting for Next. */
  autoAdvance?: boolean;
}

export interface FieldGuideConfig {
  /** Stable id for this guide's i18n copy: OnboardingFieldGuide looks up
   *  `onboarding.guides.<guideId>.…` and falls back to the inline English. */
  guideId: string;
  /** True for a phase that matches merely by being on the step's landing page,
   *  before any form is open (today: fgOpen and salesOrderOpen, both route-only).
   *  On a step the user has already finished, this phase yields to the coach
   *  card so Next is reachable without leaving the screen — the card highlights
   *  the same action, so redoing the step is still one click away. Every phase
   *  that needs a real form open keeps running, so a redo stays fully guided. */
  entryPhase?: boolean;
  fields: FieldDef[];
  submitSelector: string;
  banner: string;
  reviewTitle: string;
  reviewBody: string;
  unionSelector?: string;
  /** How to detect that this step's create form is open.
   *  - { route: string }           — form is a full-page route; match pathname prefix.
   *  - { selectorPresent: string } — form is a modal; match DOM element presence.
   *  - { anyPresent: string[] }    — active while ANY of these selectors is visible.
   *    Use when one phase spans several DOM states (e.g. a status walk that moves
   *    between a row button, an edit modal, then another row button).
   *  Unset = step uses its own detection (e.g. step 1's inventoryModalOpen). */
  openWhen?: { route: string } | { selectorPresent: string } | { anyPresent: string[] };
  /** If the primary submit opens a confirmation modal, spotlight this selector
   *  instead and show confirmInstruction until the user clicks it. The guide
   *  polls visibility on each animation frame — it switches to the confirm
   *  phase automatically once the element appears. */
  confirmSelector?: string;
  confirmInstruction?: string;
  /** When false, the dim does NOT block clicks outside the spotlight — the whole
   *  page stays interactive (the halo just points). Use when a step needs the
   *  user to touch controls beyond the single highlighted one, e.g. sliding a
   *  slider next to the highlight, or switching tabs. Defaults to true (block). */
  blockOutside?: boolean;
  /** Where to place the coach card. Defaults to 'above' (floats over the field).
   *  Use 'top-right' to pin it to the corner so a full-width control (e.g. a
   *  slider) underneath stays clear and draggable. */
  placement?: 'auto' | 'top-right' | 'above';
  /** What the guide's Back button does once there is no earlier field to return
   *  to — i.e. the guide is on its first field, every earlier field is an
   *  auto-advancing action step, or the config is review-only (`fields: []`).
   *
   *  Without this, Back was either greyed out on the first field or (for
   *  review-only configs) rendered enabled while `Math.max(0, idx - 1)` left the
   *  index at 0 — a button that visibly did nothing. Declaring the way OUT of
   *  the phase makes Back mean the same thing everywhere: undo the step the
   *  guide just told you to take.
   *
   *  - { click }  — press a real control, e.g. a modal's Cancel button. Use the
   *                 test-id of the control the user would click by hand.
   *  - { element }— press a control the guide has to look up at click time,
   *                 when a plain selector could resolve to the wrong row.
   *  - { route }  — navigate back to the list/parent page a form was opened from.
   *  - { step }   — leave the phase entirely and go back one onboarding step.
   *                 For a step's FIRST phase, where there is nothing left to
   *                 undo inside the step itself.
   *
   *  Omit it to leave Back disabled. That is the honest state for a phase whose
   *  action cannot be taken back (an order that already exists, for instance) —
   *  a greyed-out button says so, a dead one does not. */
  back?:
    | { click: string }
    | { element: () => HTMLElement | null }
    | { route: string }
    | { step: true };
}

// ---------------------------------------------------------------------------
// Step 1: Green-bean inventory item creation
// ---------------------------------------------------------------------------

export const inventoryFieldGuideConfig: FieldGuideConfig = {
  guideId: 'inventory',
  submitSelector: '[data-test-id="inventory-create-submit"]',
  banner: 'This is your real inventory — enter accurate values. It is kept after the tour.',
  reviewTitle: 'Review, then create',
  reviewBody:
    'Your form is fully visible now — check every field is correct, this is real data. When it looks right, click the highlighted Create button.',
  unionSelector: '[role="listbox"]',
  fields: [
    {
      key: 'name',
      selector: '[data-test-id="inventory-create-name"]',
      label: 'Name',
      instruction: 'Name this stock item clearly — e.g. "Ethiopia Yirgacheffe G1".',
      validate: () => inputValue('[data-test-id="inventory-create-name"]').length > 0,
    },
    {
      key: 'species',
      selector: '[data-field="green_bean_profile.species"]',
      label: 'Species',
      instruction: 'Pick the coffee species (Arabica, Robusta…).',
      validate: () => inputValue('[data-test-id="inventory-create-species"]').length > 0,
    },
    {
      key: 'process_type',
      selector: '[data-field="green_bean_profile.process_type"]',
      label: 'Process type',
      instruction: 'Choose how these beans were processed — Washed, Honey or Natural.',
      validate: () =>
        !!document.querySelector(
          '[data-field="green_bean_profile.process_type"] input[type="radio"]:checked',
        ),
    },
    {
      key: 'quantity',
      selector: '[data-test-id="inventory-create-quantity"]',
      label: 'Initial quantity',
      instruction:
        "Enter how much you actually have in stock right now. No stock yet? Leave it at 0 — that's fine, you can receive stock later.",
      // Stock may legitimately be 0 (item created before any beans arrive), so
      // any non-negative number is valid — only a blank or negative value blocks Next.
      validate: () => {
        const v = inputValue('[data-test-id="inventory-create-quantity"]');
        const n = parseFloat(v);
        return v !== '' && !Number.isNaN(n) && n >= 0;
      },
    },
    {
      key: 'cost',
      selector: '[data-test-id="inventory-create-cost"]',
      label: 'Cost',
      instruction: 'Enter the real purchase cost — this feeds your margins and reports later.',
      validate: () => {
        const digits = inputValue('[data-test-id="inventory-create-cost"]').replace(/[^0-9]/g, '');
        return digits.length > 0 && parseInt(digits, 10) > 0;
      },
    },
    {
      key: 'reorder',
      selector: '[data-test-id="inventory-create-reorder-point"]',
      label: 'Reorder warning',
      instruction:
        'Set the stock level that should trigger a low-stock alert, so you restock in time.',
      validate: () =>
        parseFloat(inputValue('[data-test-id="inventory-create-reorder-point"]')) > 0,
    },
  ],
};

// ---------------------------------------------------------------------------
// Step 3: Work Order (Production) creation (guided, user-submitted)
// ---------------------------------------------------------------------------

export const workOrderFieldGuideConfig: FieldGuideConfig = {
  guideId: 'workOrder',
  openWhen: { route: '/work-orders/create' },
  // Backing out of the first field leaves the create form for the roasting list.
  // Any draft already saved stays on the server, so nothing is lost.
  back: { route: '/work-orders' },
  submitSelector: '[data-test-id="wo-save-complete-button"]',
  confirmSelector: '[data-test-id="wo-complete-summary-confirm"]',
  confirmInstruction: 'Confirm to finish the practice roast.',
  unionSelector: '[role="listbox"]',
  banner: "This is a practice roast using your sample lot — it's removed when the tour ends.",
  reviewTitle: 'Review, then roast',
  reviewBody:
    'Check the batch details, then click Save as Complete to roast this practice batch.',
  fields: [
    {
      key: 'machine',
      selector: '[data-test-id="wo-machine-select"]',
      label: 'Machine',
      instruction: 'Pick the roast machine — we set up a sample machine for you.',
      validate: () => inputValue('[data-test-id="wo-machine-select"]').length > 0,
    },
    {
      key: 'roaster',
      selector: '[data-test-id="wo-roaster-select"]',
      label: 'Roaster',
      instruction: 'Choose who\'s roasting — pick the sample roaster.',
      validate: () => inputValue('[data-test-id="wo-roaster-select"]').length > 0,
    },
    {
      key: 'date',
      selector: '[data-test-id="wo-date-input"]',
      label: 'Date',
      instruction: 'Set the roast date.',
      validate: () => inputValue('[data-test-id="wo-date-input"]').length > 0,
    },
    {
      key: 'time',
      selector: '[data-test-id="wo-time-input"]',
      label: 'Time',
      instruction: 'Set the roast time.',
      validate: () => inputValue('[data-test-id="wo-time-input"]').length > 0,
    },
    {
      key: 'save_draft',
      selector: '[data-test-id="wo-save-draft-button"]',
      label: 'Save the basics',
      instruction:
        'Click Save as Draft to lock in these basics — that opens up the bean, weight and roast-type fields. Then hit Next to keep going.',
      // Advances once the bean section appears (it only renders after the draft
      // is saved and the work order enters edit mode). The Save-as-Draft button
      // itself is replaced once saved, so auto-advance rather than stranding the
      // user on a step whose highlight has vanished.
      validate: () => !!document.querySelector('[data-test-id="wo-green-bean-select"]'),
      autoAdvance: true,
    },
    {
      key: 'green_bean',
      selector: '[data-test-id="wo-green-bean-select"]',
      label: 'Green bean',
      instruction: 'Pick the beans to roast — choose your sample green beans.',
      validate: () => inputValue('[data-test-id="wo-green-bean-select"]').length > 0,
    },
    {
      key: 'input_weight',
      selector: '[data-test-id="wo-input-weight"]',
      label: 'Roast input',
      instruction: 'Enter how much you\'re roasting (e.g. 10 kg).',
      validate: () => parseFloat(inputValue('[data-test-id="wo-input-weight"]')) > 0,
    },
    {
      key: 'output_weight',
      selector: '[data-test-id="wo-output-weight"]',
      label: 'Roast output',
      instruction: 'Enter the roasted weight you got — a bit less than the input (e.g. 8 kg).',
      // Output is optional in the real form, but MANDATORY during the tour:
      // "Save as Complete" needs an output weight, and only a completed work
      // order satisfies the step-3 gate (work_orders.status = 'completed').
      // Letting the user skip it left them with save-as-scheduled only, which
      // never advances the tour — so they created the same practice roast twice.
      validate: () => parseFloat(inputValue('[data-test-id="wo-output-weight"]')) > 0,
    },
    {
      key: 'lots',
      selector: '[data-test-id="wo-auto-calc-lots-button"]',
      label: 'Assign lot',
      instruction: 'Click Auto-Calculate to pull from your sample lot.',
      validate: () => parseFloat(inputValue('[data-test-id="wo-lot-used-0"]')) > 0,
      autoAdvance: true,
    },
    {
      key: 'roast_type',
      selector: '[data-test-id="wo-roast-type-input"]',
      label: 'Roast type',
      instruction: 'Pick how you\'re roasting these beans.',
      validate: () => inputValue('[data-test-id="wo-roast-type-input"]').length > 0,
    },
  ],
};

// ---------------------------------------------------------------------------
// Step 4: Cupping Session (Quality Control) creation (guided, user-submitted)
// ---------------------------------------------------------------------------

export const cuppingFieldGuideConfig: FieldGuideConfig = {
  guideId: 'cupping',
  // The cupping form is a modal — detect it by its root test-id rather than a route.
  openWhen: { selectorPresent: '[data-test-id="cupping-session-create-modal"]' },
  back: { click: '[data-test-id="cupping-session-cancel"]' },
  submitSelector: '[data-test-id="cupping-session-save-draft"]',
  confirmSelector: '[data-test-id="cupping-confirm-proceed"]',
  confirmInstruction: 'Confirm to lock the cupping mode and save your practice cupping.',
  unionSelector: '[role="listbox"]',
  banner: "This is a practice cupping using your two sample roasts — it's removed when the tour ends.",
  reviewTitle: 'Review, then save',
  reviewBody:
    'Your sample cupper is already selected. Click Save Draft to log this practice cupping.',
  fields: [
    {
      key: 'roast_one',
      selector: '[data-test-id="cupping-session-bulk-select"]',
      label: 'First roast',
      instruction: 'Add your first sample roast — pick it from the list. For now, select up to 2 roasted bulks.',
      // Advance once the first roast is in the list (it renders a selected-item card).
      validate: () =>
        document.querySelectorAll('[data-test-id="cupping-selected-item"]').length >= 1,
    },
    {
      key: 'roast_two',
      selector: '[data-test-id="cupping-session-bulk-select"]',
      label: 'Second roast',
      instruction: 'Open the list again and add your second sample roast.',
      // A cupping session needs at least two roasted bulks to compare.
      validate: () =>
        document.querySelectorAll('[data-test-id="cupping-selected-item"]').length >= 2,
    },
  ],
};

// Step 4 (continued): scoring the two roasts on the session detail page. The
// session is created as a draft by cuppingFieldGuideConfig, which navigates here.
const tabCompleted = (index: number) =>
  !!document.querySelector(
    `[data-test-id="cupping-session-tabs-top"] [data-tab-index="${index}"][data-completed="true"]`,
  );

const tabActive = (index: number) =>
  !!document.querySelector(
    `[data-test-id="cupping-session-tabs-top"] [data-tab-index="${index}"][data-active="true"]`,
  );

// Every roast in the session scored. The practice cupping is capped at two, but
// a session created before that cap could hold more — keying the last field on
// tab 1 alone would then let the guide reach its review while a later roast was
// still blank, and Complete Session would refuse to submit.
const allTabsCompleted = () => {
  const tabs = Array.from(
    document.querySelectorAll('[data-test-id="cupping-session-tabs-top"] [data-tab-index]'),
  );
  return tabs.length > 0 && tabs.every((el) => el.getAttribute('data-completed') === 'true');
};

export const cuppingScoreFieldGuideConfig: FieldGuideConfig = {
  guideId: 'cuppingScore',
  // The detail/scoring page is a sub-route (/cupping-sessions/:id); the list
  // page (/cupping-sessions, no trailing slash) does not match.
  openWhen: { route: '/cupping-sessions/' },
  back: { route: '/cupping-sessions' },
  submitSelector: '[data-test-id="cupping-session-complete-button"]',
  confirmSelector: '[data-test-id="cupping-session-complete-confirm"]',
  confirmInstruction: 'Confirm to finish scoring and complete the practice cupping.',
  // The spotlight hole is the highlighted slider row itself, and a native range
  // input keeps pointer capture once a drag starts, so blocking the rest of the
  // page (the default) never gets in the way of the control we point at — while
  // leaving it unblocked let the user drag Flavour, Acidity and every other
  // slider the guide had not asked for yet. Card pinned to the corner so it
  // never sits on top of the full-width slider it is describing.
  placement: 'top-right',
  banner: "Now score your two practice roasts — drag each slider, then complete the cupping. It's removed when the tour ends.",
  reviewTitle: 'Review, then complete',
  reviewBody:
    'Both roasts are scored. Click Complete Session to finish this practice cupping.',
  fields: [
    {
      key: 'score_one',
      selector: '[data-test-id="cupping-score-row-fragrance_aroma"]',
      label: 'Score the first roast',
      instruction: 'Drag the Fragrance/Aroma slider to give the first roast a score.',
      // The tab flips to "completed" once the item's form is touched.
      validate: () => tabCompleted(0),
    },
    {
      key: 'open_second',
      // The tabs live inside the active item's form; the halo follows the visible one.
      selector: '[data-test-id="cupping-session-tabs-top"] [data-tab-index="1"]',
      label: 'Open the second roast',
      instruction: 'Click the second roast\'s tab to open it.',
      // Auto-advance to the slider step the moment the second roast is active, so
      // the halo moves down to its slider instead of stranding the user on the tab.
      validate: () => tabActive(1),
      autoAdvance: true,
    },
    {
      key: 'score_two',
      // Same row selector — pickVisibleEl resolves it to the now-active second roast.
      selector: '[data-test-id="cupping-score-row-fragrance_aroma"]',
      label: 'Score the second roast',
      instruction:
        "Now drag this roast's Fragrance/Aroma slider too — every roast in the session needs a score.",
      validate: allTabsCompleted,
    },
  ],
};

// --- Step 5: Finished Goods (Packing) -------------------------------------

// Phase 0 — switch from the Roasting tab to the Packing tab, then open the
// packing form. Route-based, so it's the active guide whenever the user is on
// /work-orders with no production modal open; the modal-based phases below
// (selectorPresent) take precedence the moment a modal opens.
export const fgOpenFieldGuideConfig: FieldGuideConfig = {
  guideId: 'fgOpen',
  openWhen: { route: '/work-orders' },
  entryPhase: true,
  // First phase of step 5: there is nothing to undo inside the step, so Back
  // leaves for the previous step rather than sitting there greyed out.
  back: { step: true },
  submitSelector: '[data-test-id="wo-create-button"]',
  banner: "You're packing your sample roast into a sellable product — a practice run that's removed when the tour ends.",
  reviewTitle: 'Open the packing form',
  reviewBody: 'Click Packing Process to start packing your sample roast.',
  fields: [
    {
      key: 'switch_packing',
      selector: '[data-test-id="wo-mode-finished-good"]',
      label: 'Switch to Packing',
      instruction: 'Roasting made your beans; Packing turns them into sellable goods. Open the Packing tab.',
      // The packing panel only mounts once the Packing tab is active.
      validate: () => !!document.querySelector('[data-test-id="fg-productions-panel"]'),
      autoAdvance: true,
    },
    {
      key: 'open_packing',
      selector: '[data-test-id="wo-create-button"]',
      label: 'Start packing',
      instruction: 'Click Packing Process to open the packing form.',
      // The packing form's create-mode marker appears once the modal opens —
      // at which point the create-phase guide below takes over.
      validate: () => !!document.querySelector('[data-test-id="fg-production-mode-create_draft"]'),
      autoAdvance: true,
    },
  ],
};

// Phase A — create the production draft (pick the roast; name/pack/qty are
// pre-filled and the single component auto-allocated to 100% by the form hook).
export const fgCreateFieldGuideConfig: FieldGuideConfig = {
  guideId: 'fgCreate',
  openWhen: { selectorPresent: '[data-test-id="fg-production-mode-create_draft"]' },
  back: { click: '[data-test-id="finished-goods-cancel"]' },
  submitSelector: '[data-test-id="finished-goods-confirm"]',
  banner: "This is a practice finished good, packed from your sample roast — it's removed when the tour ends.",
  reviewTitle: 'Review, then save',
  reviewBody: 'Check the details you entered and the roast you picked, then click Save to pack this practice finished good.',
  // Walk the create form field-by-field. Nothing is pre-filled — the user enters
  // each value themselves, just like the real form; each field unlocks Next once
  // it has a valid value.
  fields: [
    {
      key: 'name',
      selector: '[data-test-id="finished-goods-final-name"]',
      label: 'Name',
      instruction: 'Type a name for this finished good — e.g. "House Blend 250g".',
      validate: () => inputValue('[data-test-id="finished-goods-final-name"]').length > 0,
    },
    {
      key: 'pack_size',
      selector: '[data-test-id="finished-goods-final-packsize"]',
      label: 'Pack size',
      instruction: 'Enter the weight of one pack — e.g. 0.25 kg.',
      validate: () => parseFloat(inputValue('[data-test-id="finished-goods-final-packsize"]')) > 0,
    },
    {
      key: 'pick_roast',
      selector: '[data-test-id="finished-goods-component-select-0"]',
      label: 'Choose the roast to pack',
      instruction: 'Pick your sample roast from the list — this is the bulk you roasted earlier.',
      // The roast-type readout shows "-" before a roast is selected and the
      // actual roast type (e.g. "MEDIUM") after — unlike the available-kg div
      // which renders "0.000" as soon as the row is added regardless of selection.
      // This is the earliest reliable signal that a roast has been chosen.
      validate: () => {
        const el = visibleEl('[data-test-id="finished-goods-component-roast-0"]');
        const text = el?.textContent?.trim() ?? '';
        return text.length > 0 && text !== '-';
      },
    },
    {
      key: 'quantity',
      selector: '[data-test-id="finished-goods-final-quantity"]',
      label: 'Quantity',
      instruction: "Enter how many packs you're making — e.g. 10.",
      validate: () => Number(inputValue('[data-test-id="finished-goods-final-quantity"]')) > 0,
    },
    {
      key: 'price',
      selector: '[data-test-id="finished-goods-estimated-price"]',
      label: 'Selling price',
      instruction: 'Enter the selling price per pack — e.g. Rp 100,000.',
      validate: () => {
        const d = inputValue('[data-test-id="finished-goods-estimated-price"]').replace(/[^0-9]/g, '');
        return d.length > 0 && parseInt(d, 10) > 0;
      },
    },
  ],
};

// The panel lists the company's REAL productions alongside the tour's practice
// draft, and any draft row renders the same `fg-production-complete-<id>` button.
// Matching that prefix alone made the tour spotlight whichever draft happened to
// come first and tell the user to complete it — real production included. The
// panel row and the modal-mode marker both carry `data-onboarding-sample="true"`
// only for the draft recorded in sampleRefs.fgProductionId (stamped in
// FgProductionsPanel / FinishedGoodsProductionModal), so scoping every step-5
// completion phase to that marker makes the tour drive the practice draft and
// nothing else. No marker (e.g. a tour that packed its draft before this existed)
// means no phase matches, and the guide falls back to creating a fresh practice
// draft rather than pointing at real data.
const FG_SAMPLE_MARK = '[data-onboarding-sample="true"]';
const FG_SAMPLE_COMPLETE_BTN = `${FG_SAMPLE_MARK} [data-test-id^="fg-production-complete-"]`;

// Phase B — complete the auto-opened draft (everything carries over; just confirm).
export const fgCompleteFieldGuideConfig: FieldGuideConfig = {
  guideId: 'fgComplete',
  openWhen: { selectorPresent: `[data-test-id="fg-production-mode-complete_draft"]${FG_SAMPLE_MARK}` },
  // Review-only, so Back has no earlier field to fall back to: close the modal
  // and return to the Packing tab, which is what "discard changes" does by hand.
  back: { click: '[data-test-id="finished-goods-cancel"]' },
  submitSelector: '[data-test-id="finished-goods-confirm"]',
  banner: "Last step — complete the production to put the finished good in stock. It's removed when the tour ends.",
  reviewTitle: 'Complete the production',
  reviewBody: 'Everything carries over from your draft. Click Complete to create sellable stock.',
  // Review-only (no fields) so the page is NOT dimmed — just the Complete button
  // is highlighted. Clicking it submits the production and advances the step.
  fields: [],
};

// Phase B-open — after Save closes the create form, the draft appears in the
// panel as a row with a "Complete" action. Spotlight that button so the user
// opens the completion step themselves; clicking it opens the complete modal,
// at which point fgCompleteFieldGuideConfig takes over. The per-row Complete
// button only exists for draft rows, so its presence detects this phase.
export const fgCompletePanelFieldGuideConfig: FieldGuideConfig = {
  guideId: 'fgCompletePanel',
  openWhen: { selectorPresent: FG_SAMPLE_COMPLETE_BTN },
  // Back reopens the draft the user just saved (the row's View action) so they
  // can read the packed figures again — the panel row itself has nothing else
  // to step back to. fgViewFieldGuideConfig below then takes over.
  //
  // Resolved at click time rather than by a static selector: derive the id from
  // the sample row's Complete button the halo is already pointing at, so View
  // lands on that same row rather than on whichever real production sorts first.
  back: {
    element: () => {
      const complete = visibleEl(FG_SAMPLE_COMPLETE_BTN);
      const id = complete?.getAttribute('data-test-id')?.slice('fg-production-complete-'.length);
      return id
        ? document.querySelector<HTMLElement>(`[data-test-id="fg-production-view-${id}"]`)
        : null;
    },
  },
  submitSelector: FG_SAMPLE_COMPLETE_BTN,
  banner: 'Your practice draft is saved. Now complete it to put the finished good in stock — removed when the tour ends.',
  reviewTitle: 'Complete your draft',
  reviewBody: 'Click Complete on your draft row to open the completion step.',
  // Review-only (no fields) so the page is NOT dimmed — just the panel's Complete
  // button is highlighted. Clicking it opens the complete modal, at which point
  // fgCompleteFieldGuideConfig takes over.
  fields: [],
};

// Phase B-view — the user pressed Back on the panel phase, which reopened the
// saved draft read-only (mode 'view'). Without a config for this DOM state the
// overlay fell through to the step's coach card and the tour looked lost. It is
// review-only: read the draft, then Back closes it and the panel phase resumes.
export const fgViewFieldGuideConfig: FieldGuideConfig = {
  guideId: 'fgView',
  openWhen: { selectorPresent: `[data-test-id="fg-production-mode-view"]${FG_SAMPLE_MARK}` },
  // Nothing to submit in view mode — point at Close so the halo has a target.
  submitSelector: '[data-test-id="finished-goods-cancel"]',
  back: { click: '[data-test-id="finished-goods-cancel"]' },
  banner: 'This is the practice draft you saved — reading it changes nothing. It is removed when the tour ends.',
  reviewTitle: 'Your saved draft',
  reviewBody: 'Check the figures you packed, then close this to go back and complete the draft.',
  fields: [],
};

// --- Step 6: Selling ------------------------------------------------------

// Phase 0 — open the create form. Route-based, so it's the active guide whenever
// the user is on /sales-orders with no order yet and no modal open; the
// create-modal phase (selectorPresent) and the status walk (anyPresent) below
// take precedence the moment their elements appear.
export const salesOrderOpenFieldGuideConfig: FieldGuideConfig = {
  guideId: 'salesOrderOpen',
  openWhen: { route: '/sales-orders' },
  entryPhase: true,
  // First phase of step 6 — same reasoning as fgOpen above.
  back: { step: true },
  submitSelector: '[data-test-id="so-create-button"]',
  banner: "You're selling the finished good you just packed — a practice sale that's removed when the tour ends.",
  reviewTitle: 'Open the sales form',
  reviewBody: 'Click Create Sales Order to start your practice sale.',
  fields: [
    {
      key: 'open_form',
      selector: '[data-test-id="so-create-button"]',
      label: 'Start the sale',
      instruction: 'Click Create Sales Order to open the sales form.',
      // The create form's marker appears once the modal opens — at which point
      // the create-phase guide below takes over.
      validate: () => !!document.querySelector('[data-test-id="sales-order-create-form"]'),
      autoAdvance: true,
    },
  ],
};

// Phase A — create the sales order (draft) selling the finished good just packed.
// Nothing is pre-filled: the user enters each value themselves, just like the
// real form. Each field unlocks Next once it has a valid value.
export const salesOrderCreateFieldGuideConfig: FieldGuideConfig = {
  guideId: 'salesOrderCreate',
  openWhen: { selectorPresent: '[data-test-id="sales-order-create-form"]' },
  back: { click: '[data-test-id="so-create-cancel"]' },
  submitSelector: '[data-test-id="so-create-submit"]',
  unionSelector: '[role="listbox"]',
  banner: 'This is a practice sale of the finished good you just packed — removed when the tour ends.',
  reviewTitle: 'Review, then save',
  reviewBody: 'Check the customer, shipping address, finished good, quantity, price and shipping cost, then click Save to log this practice sales order as a draft.',
  fields: [
    {
      key: 'customer',
      selector: '[data-test-id="so-customer-input"]',
      label: 'Customer',
      instruction: 'Pick who you\'re selling to — choose the sample customer from the list.',
      validate: () => inputValue('[data-test-id="so-customer-input"]').length > 0,
    },
    {
      key: 'shipping_address',
      selector: '[data-test-id="so-shipping-address"]',
      label: 'Shipping address',
      instruction: 'Enter where the order ships to — the sample customer has no address on file yet.',
      validate: () => inputValue('[data-test-id="so-shipping-address"]').length > 0,
    },
    {
      key: 'pick_fg',
      selector: '[data-test-id="so-item-product-select-0"]',
      label: 'Choose the finished good',
      instruction: 'Pick the finished good you just packed — this is what you\'re selling.',
      validate: () => inputValue('[data-test-id="so-item-product-select-0"]').length > 0,
    },
    {
      key: 'qty',
      selector: '[data-test-id="so-create-item-quantity-0"]',
      label: 'Quantity',
      instruction: 'Enter how many packs to sell — e.g. 5.',
      validate: () => Number(inputValue('[data-test-id="so-create-item-quantity-0"]')) > 0,
    },
    {
      key: 'price',
      selector: '[data-test-id="so-item-price-input-0"]',
      label: 'Unit price',
      instruction: 'Enter the selling price per pack — e.g. Rp 100,000.',
      // The price input is formatted (e.g. "Rp 100.000"), so strip non-digits
      // before checking it's a real amount — Number("Rp 100.000") is NaN.
      validate: () => {
        const d = inputValue('[data-test-id="so-item-price-input-0"]').replace(/[^0-9]/g, '');
        return d.length > 0 && parseInt(d, 10) > 0;
      },
    },
    {
      key: 'shipping_cost',
      selector: '[data-test-id="so-shipping-cost"]',
      label: 'Shipping cost',
      instruction: 'Enter the shipping cost for this order — leave it at Rp 0 if there\'s no shipping fee.',
      // Formatted (Rupiah) and legitimately zero (free shipping / pickup), so
      // accept any non-negative amount — the field defaults to "Rp 0".
      validate: () => {
        const d = inputValue('[data-test-id="so-shipping-cost"]').replace(/[^0-9]/g, '');
        return d.length > 0 && parseInt(d, 10) >= 0;
      },
    },
  ],
};

// Phase B walks the order Draft → Sent → Delivered. Each "Mark as" opens a
// prefilled edit modal that the user saves; the page dispatches the delivered
// event once it lands, which advances the tour. The walk is split into a row
// phase and a modal phase, both review-only (no fields) so neither the list nor
// the edit modal is dimmed — just the relevant button is ringed (matching the
// finished-good complete phases). The user clicks through naturally and the DOM
// state decides which phase is active.

// Phase B (row) — highlight the status button on the order's row: Mark as Sent
// while it's a Draft, then Mark as Delivered once it's Sent. anyPresent (not the
// bare route) so it activates only after an order exists — the route alone would
// hijack the empty list page before the user has created anything.
export const salesOrderStatusRowFieldGuideConfig: FieldGuideConfig = {
  guideId: 'salesOrderStatusRow',
  // The list renders BOTH layouts and hides one with CSS, and the mobile card
  // names its buttons `so-mobile-mark-*` rather than `so-mark-*`. Matching only
  // the desktop prefix left this phase dead below the md breakpoint: nothing
  // matched, the overlay fell through to salesOrderOpen (whose openWhen is just
  // the route, so it always matches) and the guide asked for a second sales
  // order the user had already created. Each selector is tested on its own, so
  // the hidden layout's copy simply fails its own visibility check.
  openWhen: {
    anyPresent: [
      '[data-test-id^="so-mark-sent-"]',
      '[data-test-id^="so-mark-delivered-"]',
      '[data-test-id^="so-mobile-mark-sent-"]',
      '[data-test-id^="so-mobile-mark-delivered-"]',
    ],
  },
  // Only one mark button exists per order at a time (Draft shows Sent, Sent shows
  // Delivered); pickVisibleEl resolves the combined selector to whichever is live.
  submitSelector:
    '[data-test-id^="so-mark-sent-"], [data-test-id^="so-mark-delivered-"], [data-test-id^="so-mobile-mark-sent-"], [data-test-id^="so-mobile-mark-delivered-"]',
  banner: "Now walk your order through its lifecycle: Draft → Sent → Delivered. It's removed when the tour ends.",
  reviewTitle: 'Move the order forward',
  reviewBody:
    'Click the highlighted status button to move your order to its next stage — Sent, then Delivered.',
  // No `back`: the order already exists and a status advance is not reversible
  // from here, so Back stays disabled rather than pretending otherwise.
  fields: [],
};

// Phase B (modal) — the Mark-as edit modal is open, pre-filled with the next
// status; highlight Save. Listed before the row phase so it wins while the edit
// modal is open (the row buttons stay laid out behind it).
export const salesOrderStatusModalFieldGuideConfig: FieldGuideConfig = {
  guideId: 'salesOrderStatusModal',
  openWhen: { selectorPresent: '[data-test-id="sales-order-edit-form"]' },
  // Review-only: Back closes the mark-as modal, exactly like its Cancel button.
  back: { click: '[data-test-id="so-create-cancel"]' },
  submitSelector: '[data-test-id="so-create-submit"]',
  banner: 'Confirm the new status to move your practice order forward — removed when the tour ends.',
  reviewTitle: 'Confirm the status',
  reviewBody: 'Everything carries over from the order. Click Save to confirm its new status.',
  fields: [],
};

// ---------------------------------------------------------------------------
// Step 2: Purchase Order creation (guided, user-submitted)
// ---------------------------------------------------------------------------

export const purchaseOrderFieldGuideConfig: FieldGuideConfig = {
  guideId: 'purchaseOrder',
  openWhen: { route: '/purchase-orders/create' },
  // Nothing is persisted until Save, so backing out of the first field just
  // returns to the purchasing list.
  back: { route: '/purchase-orders' },
  submitSelector: '[data-test-id="po-create-submit"]',
  unionSelector: '[role="listbox"]',
  banner: "This is a practice purchase order using your sample supplier — it's removed when the tour ends.",
  reviewTitle: 'Review, then create',
  reviewBody:
    'Check the supplier, item and quantity look right, then click the highlighted Create button to place this practice order.',
  fields: [
    {
      key: 'supplier',
      selector: '[data-test-id="po-create-supplier-input"]',
      label: 'Supplier',
      instruction: 'Pick your supplier — we seeded a sample supplier for you to use.',
      validate: () => inputValue('[data-test-id="po-create-supplier-input"]').length > 0,
    },
    {
      key: 'item',
      selector: '[data-test-id="po-create-item-name-0"]',
      label: 'Item',
      instruction: "Choose what you're buying — pick the sample green beans.",
      validate: () => inputValue('[data-test-id="po-create-item-name-0"]').length > 0,
    },
    {
      key: 'quantity',
      selector: '[data-test-id="po-create-item-quantity-0"]',
      label: 'Order quantity',
      instruction: "Enter how much you're ordering (e.g. 10 kg).",
      validate: () => parseFloat(inputValue('[data-test-id="po-create-item-quantity-0"]')) > 0,
    },
    {
      key: 'delivery_status',
      selector: '[data-test-id="po-create-delivery-status"]',
      label: 'Delivery status',
      instruction: 'Mark this order as Received so the beans land in your stock as a lot.',
      validate: () => inputValue('[data-test-id="po-create-delivery-status"]') === 'received',
    },
    {
      key: 'arrival_weight',
      selector: '[data-test-id="po-create-item-arrival-0"]',
      label: 'Arrival weight',
      instruction: 'Enter the weight that actually arrived.',
      validate: () => parseFloat(inputValue('[data-test-id="po-create-item-arrival-0"]')) > 0,
    },
    {
      key: 'unit_price',
      selector: '[data-test-id="po-create-item-unit-price-0"]',
      label: 'Unit price',
      instruction: 'Enter the price you paid per kg.',
      validate: () => {
        const d = inputValue('[data-test-id="po-create-item-unit-price-0"]').replace(/[^0-9]/g, '');
        return d.length > 0 && parseInt(d, 10) > 0;
      },
    },
    {
      key: 'payment_type',
      selector: '[data-test-id="po-create-payment-type"]',
      label: 'Payment type',
      instruction: 'Pick how you paid.',
      validate: () => inputValue('[data-test-id="po-create-payment-type"]').length > 0,
    },
  ],
};
